/* ============================================================
   <product-editor> — éditeur de produit (frontend)
   ------------------------------------------------------------
   - Couleurs : change la variante -> met à jour TOUTES les images
     d'angle + les zones, sans effacer les logos uploadés.
   - Angles : chaque angle a son image (variante.image ou metafield)
     et ses zones imprimables (metafields custom.zone_* de type json).
   - Zones : lues depuis les metafields (coords normalisées 0..1),
     dessinées par-dessus l'image, exactes et responsives.
   - Logos : un logo par zone, déplaçable / redimensionnable,
     BORNÉ à l'intérieur de sa zone.
   - État sérialisé dans <input hidden> pour le backend.

   Le thème ne fait que LIRE les metafields (jamais d'écriture).

   API : document.querySelector('product-editor').getState()
   Event: 'product-editor:change' (detail = state)
   ============================================================ */

class ProductEditor extends HTMLElement {
  connectedCallback() {
    this.layer = this.querySelector('[data-pe-zones-layer]');
    this.productImage = this.querySelector('[data-pe-product-image]');
    this.stateInput = this.querySelector('[data-pe-state]');
    this.uploadStatus = this.querySelector('[data-pe-upload-status]');
    // texte de la zone : contrôlé par le réglage de section, vide = aucun texte
    this.zoneLabel = this.dataset.zoneLabel || '';
    // anciennes valeurs mémorisées dans les réglages du template : on les remplace
    if (/^(Choose Artwork|Ajoutez votre logo|Déposez votre logo ici)$/.test(this.zoneLabel)) {
      this.zoneLabel = '';
    }
    this.backendUrl = (this.dataset.backendUrl || '').replace(/\/+$/, '');
    this.feeVariantSmall = this.dataset.feeVariantSmall || '';
    this.feeVariantLarge = this.dataset.feeVariantLarge || '';
    this.feePriceSmall = parseInt(this.dataset.feePriceSmall, 10) || 300; // cents
    this.feePriceLarge = parseInt(this.dataset.feePriceLarge, 10) || 500;
    this.feeVariantEmbroidery = this.dataset.feeVariantEmbroidery || '';
    this.feePriceEmbroidery = parseInt(this.dataset.feePriceEmbroidery, 10) || 1000;
    this.feeVariantDigitization = this.dataset.feeVariantDigitization || '';
    this.feePriceDigitization = parseInt(this.dataset.feePriceDigitization, 10) || 7000;
    // Types réellement autorisés sur ce produit (tags no-dtf / no-broderie).
    // Ils servent de VERROU : un type masqué ne doit jamais atteindre le panier,
    // quelle que soit la provenance de la valeur (état restauré, DOM modifié...).
    this.allowDtf = this.dataset.allowDtf !== 'false';
    this.allowEmbroidery = this.dataset.allowEmbroidery === 'true';
    // type par défaut : 'embroidery' si le produit est broderie seule (tag no-dtf)
    this.defaultPrintType = this.dataset.defaultPrintType === 'embroidery' ? 'embroidery' : 'dtf';
    this.logoDigitized = false; // déclaré "déjà numérisé" par le client
    this.embroideryMin = parseInt(this.dataset.embroideryMin, 10) || 12; // min d'articles si broderie
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    // angle initial = premier angle réellement affiché (les angles sans image
    // de côté ne sont pas rendus par la section)
    const firstArea = this.querySelector('[data-pe-area]');
    this.currentAngle = firstArea ? Number(firstArea.dataset.index) || 0 : 0;
    this.currentVariantId = this.dataset.initialVariant;
    this.selectedZoneId = null;

    // logos : clé "angleIndex:zoneId" -> { dataUrl, fileName, zoneId, x, y, w, rotate }
    this.designs = {};

    const active = this.querySelector('[data-pe-swatch].is-active, [data-pe-bulk-color].is-active');
    this.color = active ? active.dataset.color || active.dataset.value : null;

    this.variants = this.parseJSON('[data-pe-variants]', []);
    this.variantImages = this.parseJSON('[data-pe-variant-images]', {});
    this.variantZones = this.parseJSON('[data-pe-variant-zones]', {});
    this.i18n = this.parseJSON('[data-pe-i18n]', {});

    this.bindOptions();
    this.bindAngles();
    this.bindUpload();
    this.bindLogoModal();
    this.bindControls();
    this.bindCart();
    this.bindBulk();
    this.bindPrintType();
    this.bindImageProtection();

    // incitatif rabais de volume : quantité déjà au panier (frais exclus)
    this.cartQty = 0;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        this.cartQty = (cart.items || []).reduce(
          (n, it) => (it.properties && it.properties._fee ? n : n + it.quantity),
          0
        );
        this.updateVolumeIncentive();
      })
      .catch(() => this.updateVolumeIncentive());

    // rendu initial des zones (après chargement de l'image pour de bonnes mesures)
    if (this.productImage && !this.productImage.complete) {
      this.productImage.addEventListener('load', () => this.renderStage(), { once: true });
    }
    this.renderStage();
    window.addEventListener('resize', () => this.repositionAll());

    // Mode édition : ?pe_edit=<line_key> recharge un article du panier
    this.editLineKey = null;
    this.maybeRestoreFromCart();
  }

  /* ---------------- Protection des visuels produit ----------------
     Bloque le menu contextuel et le glisser-déposer sur les images du produit
     (aperçu + vignettes d'angles). Dissuasif : les champs de saisie gardent
     leur menu contextuel, et le dépôt de fichiers logo reste fonctionnel. */
  bindImageProtection() {
    const isProtected = (target) =>
      target &&
      target.closest &&
      target.closest('.pe-stage, [data-pe-area], .pe-thumb, .pe-zones-layer');

    const block = (e) => {
      if (isProtected(e.target)) e.preventDefault();
    };
    this.addEventListener('contextmenu', block);
    this.addEventListener('dragstart', block);
  }

  parseJSON(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector).textContent);
    } catch (e) {
      return fallback;
    }
  }

  // Traduction (clés injectées par la section depuis locales/xx.json) ;
  // repli français si la clé manque.
  t(key, fallback) {
    return (this.i18n && this.i18n[key]) || fallback || key;
  }

  /* ---------------- Données image / zones ---------------- */
  getAngleImage(angleIndex, variantId) {
    const imgs = this.variantImages[variantId || this.currentVariantId];
    if (imgs && imgs[angleIndex]) return imgs[angleIndex];
    if (imgs) return imgs.find(Boolean) || '';
    return '';
  }

  // Zones actives d'un angle donné pour la variante courante.
  getZonesForAngle(angleIndex) {
    const perAngle = this.variantZones[this.currentVariantId];
    const obj = perAngle ? perAngle[angleIndex] : null;
    const areas = obj && Array.isArray(obj.areas) ? obj.areas : [];
    const active = areas.filter((a) => a.active !== false);
    if (active.length > 0) return active;
    // Aucune zone définie -> zone implicite = image entière (non dessinée)
    return [{ id: '_full', x: 0, y: 0, width: 1, height: 1, _implicit: true, label: '' }];
  }

  getCurrentZones() {
    return this.getZonesForAngle(this.currentAngle);
  }

  /* ---------------- Type d'impression (DTF / broderie, PAR logo) ---------------- */
  bindPrintType() {
    this.querySelectorAll('[data-pe-print-type]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        this.defaultPrintType = this.normalizePrintType(radio.value);
        this.updatePrintTypeNote();
        // applique le choix au logo sélectionné
        const design = this.designs[this.currentAngle + ':' + this.selectedZoneId];
        if (design) {
          design.printType = this.normalizePrintType(radio.value);
          this.serialize();
        } else {
          this.updateFees();
        }
      });
    });
    // "logo déjà numérisé lors d'une commande précédente" => pas de frais 70 $
    const digitized = this.querySelector('[data-pe-digitized]');
    if (digitized) {
      digitized.addEventListener('change', () => {
        this.logoDigitized = digitized.checked;
        this.updateFees();
        this.serialize();
      });
    }

    this.updatePrintTypeNote();
  }

  // Note "max 6 po × 6 po" + case "déjà numérisé" : broderie uniquement
  updatePrintTypeNote() {
    const checked = this.querySelector('[data-pe-print-type]:checked');
    const isEmbroidery = !!checked && checked.value === 'embroidery';
    this.querySelectorAll('[data-pe-ptype-extra]').forEach((el) => {
      el.classList.toggle('hidden', !isEmbroidery);
    });
  }

  // Ramène un type d'impression dans ce que le produit autorise réellement.
  // Sans ça, un logo resté en 'dtf' sur un produit tagué no-dtf générait
  // quand même une ligne de frais DTF au panier.
  normalizePrintType(type) {
    if (type === 'embroidery') return this.allowEmbroidery ? 'embroidery' : 'dtf';
    return this.allowDtf ? 'dtf' : 'embroidery';
  }

  // Reflète le type du logo sélectionné dans les boutons radio
  syncPrintTypeRadios() {
    const design = this.designs[this.currentAngle + ':' + this.selectedZoneId];
    const type = this.normalizePrintType(design ? design.printType : this.defaultPrintType);
    this.querySelectorAll('[data-pe-print-type]').forEach((radio) => {
      radio.checked = radio.value === type;
    });
    this.updatePrintTypeNote();
  }

  /* ---------------- Frais d'impression (1 frais PAR logo) ---------------- */
  // Chaque logo est facturé selon son type : DTF (variante "grand format", 5 $)
  // ou broderie (10 $). La mesure en cm reste informative pour la production.
  computeFeeCounts() {
    let dtf = 0;
    let embroidery = 0;
    Object.keys(this.designs).forEach((key) => {
      if (this.normalizePrintType(this.designs[key].printType) === 'embroidery') embroidery++;
      else dtf++;
    });
    // numérisation : UNE fois par logo brodé, jamais multipliée par la
    // quantité de vêtements ; zéro si le logo est déclaré déjà numérisé
    const digitization = this.logoDigitized ? 0 : embroidery;
    return { small: 0, large: dtf, embroidery: embroidery, digitization: digitization };
  }

  formatMoney(cents) {
    const v = cents / 100;
    const n = (fix, dsep, tsep) => {
      const parts = v.toFixed(fix).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, tsep);
      return parts.join(dsep);
    };
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, n(2, '.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, n(0, '.', ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, n(2, ',', '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, n(0, ',', '.'))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, n(2, '.', "'"));
  }

  // Met à jour le panneau "Frais d'impression" sous le prix
  updateFees() {
    const panel = this.querySelector('[data-pe-fees]');
    if (!panel) return;
    const rowsEl = panel.querySelector('[data-pe-fees-rows]');
    const totalEl = panel.querySelector('[data-pe-fees-total]');
    const counts = this.computeFeeCounts();

    if (counts.small + counts.large + counts.embroidery === 0) {
      panel.classList.add('hidden');
      // le minimum broderie dépend des logos : re-valider le bouton d'ajout
      if (this._bulkReady) this.updateBulkSummary();
      return;
    }
    panel.classList.remove('hidden');

    let html = '';
    if (counts.small > 0) {
      html +=
        '<li><span>' + this.t('print_fees', 'Frais d’impression') +
        (counts.small > 1 ? ' × ' + counts.small : '') +
        '</span><span>' +
        this.formatMoney(counts.small * this.feePriceSmall) +
        '</span></li>';
    }
    if (counts.large > 0) {
      html +=
        '<li><span>' + this.t('print_dtf', 'Impression DTF') +
        (counts.large > 1 ? ' × ' + counts.large : '') +
        '</span><span>' +
        this.formatMoney(counts.large * this.feePriceLarge) +
        '</span></li>';
    }
    if (counts.embroidery > 0) {
      html +=
        '<li><span>' + this.t('print_embroidery', 'Broderie') +
        (counts.embroidery > 1 ? ' × ' + counts.embroidery : '') +
        '</span><span>' +
        this.formatMoney(counts.embroidery * this.feePriceEmbroidery) +
        '</span></li>';
    }
    if (counts.digitization > 0) {
      html +=
        '<li><span>' + this.t('digitization_row', 'Numérisation broderie (unique)') +
        (counts.digitization > 1 ? ' × ' + counts.digitization : '') +
        '</span><span>' +
        this.formatMoney(counts.digitization * this.feePriceDigitization) +
        '</span></li>';
    }
    if (rowsEl) rowsEl.innerHTML = html;

    const variant = this.variants.find((v) => String(v.id) === String(this.currentVariantId));
    const total =
      (variant && variant.price ? variant.price : 0) +
      counts.small * this.feePriceSmall +
      counts.large * this.feePriceLarge +
      counts.embroidery * this.feePriceEmbroidery +
      counts.digitization * this.feePriceDigitization;
    if (totalEl) totalEl.textContent = this.formatMoney(total);

    // le minimum broderie dépend des logos : re-valider le bouton d'ajout
    if (this._bulkReady) this.updateBulkSummary();
  }

  // Taille physique du logo en cm, déduite des mesures de sa zone (null si zone non mesurée)
  logoSizeCm(design, angleIndex) {
    const zone = this.getZonesForAngle(angleIndex).find(
      (z) => String(z.id) === String(design.zoneId)
    );
    if (!zone || !zone.widthCm || !zone.heightCm || !zone.width || !zone.height) return null;
    if (design.w == null || design.h == null) return null;
    return {
      widthCm: (design.w / zone.width) * zone.widthCm,
      heightCm: (design.h / zone.height) * zone.heightCm,
    };
  }

  getZoneById(id) {
    return this.getCurrentZones().find((z) => String(z.id) === String(id)) || null;
  }

  applyAngleImage() {
    if (!this.productImage) return;
    const src = this.getAngleImage(this.currentAngle);
    if (src) this.productImage.src = src;
  }

  refreshAreaThumbs() {
    this.querySelectorAll('[data-pe-area]').forEach((area) => {
      const index = Number(area.dataset.index);
      const img = area.querySelector('[data-pe-area-img]');
      const src = this.getAngleImage(index);
      if (img && src) img.src = src;
    });
  }

  /* ---------------- Options / variantes ---------------- */
  bindOptions() {
    const cur = this.variants.find((v) => String(v.id) === String(this.currentVariantId));
    this.selectedOptions = cur && cur.options ? cur.options.slice() : [];

    this.querySelectorAll('[data-pe-swatch],[data-pe-pill]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.optionIndex || 0);
        const val = btn.dataset.value != null ? btn.dataset.value : btn.dataset.color;
        this.selectedOptions[idx] = val;

        // état actif dans le même groupe d'option
        const group = btn.closest('[data-pe-option-group]') || btn.parentElement;
        group.querySelectorAll('[data-pe-swatch],[data-pe-pill]').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // libellés
        if (btn.hasAttribute('data-pe-swatch')) {
          this.color = val;
          const nameEl = this.querySelector('[data-pe-color-name]');
          if (nameEl) nameEl.textContent = val;
        } else {
          const nameEl = this.querySelector(`[data-pe-option-name="${idx}"]`);
          if (nameEl) nameEl.textContent = val;
        }

        this.applyVariantSelection();
      });
    });
  }

  findVariant() {
    return this.variants.find(
      (v) =>
        v.options &&
        v.options.length === this.selectedOptions.length &&
        v.options.every(
          (o, i) => String(o).toLowerCase() === String(this.selectedOptions[i]).toLowerCase()
        )
    );
  }

  /* Reattache chaque design aux zones de la variante courante : les ids de
     zones diffèrent d'une variante à l'autre (metafields distincts), et leurs
     rectangles peuvent bouger. On retrouve la zone équivalente (même id, sinon
     même placement, sinon même position) et on transpose les coordonnées
     relativement au nouveau rectangle — le logo reste "à la même place". */
  remapDesignsToZones() {
    const remapped = {};
    Object.keys(this.designs).forEach((key) => {
      const angleIndex = Number(key.split(':')[0]);
      const design = this.designs[key];
      const zones = this.getZonesForAngle(angleIndex);

      let zone = zones.find((z) => String(z.id) === String(design.zoneId));
      if (!zone && design.zonePlacement) {
        zone = zones.find((z) => z.placement === design.zonePlacement);
      }
      if (!zone) zone = zones[design.zoneIndex] || zones[0];
      if (!zone) {
        remapped[key] = design;
        return;
      }

      // transposition proportionnelle de l'ancien rectangle vers le nouveau
      const oldRect = design.zoneRect;
      if (
        String(zone.id) !== String(design.zoneId) &&
        oldRect && oldRect.width > 0 && oldRect.height > 0
      ) {
        const relX = (design.x - oldRect.x) / oldRect.width;
        const relY = (design.y - oldRect.y) / oldRect.height;
        const relW = design.w / oldRect.width;
        design.x = zone.x + relX * zone.width;
        design.y = zone.y + relY * zone.height;
        design.w = relW * zone.width;
        if (design.h != null && oldRect.height > 0) {
          design.h = (design.h / oldRect.height) * zone.height;
        }
      }

      if (String(this.selectedZoneId) === String(design.zoneId)) {
        this.selectedZoneId = zone.id;
      }
      design.zoneId = zone.id;
      design.zoneRect = { x: zone.x, y: zone.y, width: zone.width, height: zone.height };
      remapped[angleIndex + ':' + zone.id] = design;
    });
    this.designs = remapped;
  }

  applyVariantSelection() {
    let variant = this.findVariant();
    // combinaison exacte introuvable : retomber sur une variante de la couleur choisie
    if (!variant && this.colorOptionIndex > -1) {
      const wanted = String(this.selectedOptions[this.colorOptionIndex] || '').toLowerCase();
      const matches = this.variants.filter(
        (v) => v.options && String(v.options[this.colorOptionIndex]).toLowerCase() === wanted
      );
      variant = matches.find((v) => v.available) || matches[0];
    }
    if (variant) {
      this.currentVariantId = String(variant.id);
      this.remapDesignsToZones();
      this.refreshAreaThumbs();
      this.applyAngleImage();
      this.updatePrice(variant);
      this.updateCartButton(variant);
      if (this.productImage && !this.productImage.complete) {
        this.productImage.addEventListener('load', () => this.renderStage(), { once: true });
      } else {
        this.renderStage();
      }
    }
    this.serialize();
  }

  updatePrice(variant) {
    const el = this.querySelector('[data-pe-price]');
    if (el && variant.priceFormatted) el.textContent = variant.priceFormatted;
  }

  updateCartButton(variant) {
    const btn = this.querySelector('[data-pe-add]');
    const label = this.querySelector('[data-pe-add-label]');
    if (!btn) return;
    btn.disabled = !variant.available;
    if (label) {
      label.textContent = variant.available
        ? this.editLineKey
          ? this.t('update_item', 'Mettre à jour mon article')
          : this.t('add_to_cart', 'Ajouter au panier')
        : this.t('sold_out', 'Épuisé');
    }
  }

  /* ---------------- Angles ---------------- */
  bindAngles() {
    const select = (index, name) => {
      this.currentAngle = index;
      this.selectedZoneId = null;

      this.querySelectorAll('[data-pe-area]').forEach((a) =>
        a.classList.toggle('is-active', Number(a.dataset.index) === index)
      );

      this.applyAngleImage();

      const angleName = this.querySelector('[data-pe-angle-name]');
      if (angleName && name) angleName.textContent = name;
      const uploadAngle = this.querySelector('[data-pe-upload-angle]');
      if (uploadAngle && name) uploadAngle.textContent = name;

      if (this.productImage && !this.productImage.complete) {
        this.productImage.addEventListener('load', () => this.renderStage(), { once: true });
      } else {
        this.renderStage();
      }
    };

    this.querySelectorAll('[data-pe-area]').forEach((area) => {
      area.addEventListener('click', (e) => {
        if (e.target.closest('[data-pe-area-close]')) return;
        select(Number(area.dataset.index), area.dataset.name);
      });
    });

    // bouton "retirer" sur la vignette d'angle : enlève tous les logos de l'angle
    this.querySelectorAll('[data-pe-area-close]').forEach((close) => {
      close.addEventListener('click', (e) => {
        e.stopPropagation();
        const area = close.closest('[data-pe-area]');
        const index = Number(area.dataset.index);
        Object.keys(this.designs).forEach((k) => {
          if (k.startsWith(index + ':')) delete this.designs[k];
        });
        area.classList.remove('has-design');
        if (index === this.currentAngle) this.renderStage();
        this.serialize();
      });
    });
  }

  /* ---------------- Rendu du stage (zones + logos) ---------------- */
  renderStage() {
    if (!this.layer) return;
    this.layer.innerHTML = '';

    const zones = this.getCurrentZones();

    // 1) Dessiner les zones (sauf zone implicite "image entière")
    zones.forEach((z) => {
      if (z._implicit) return;
      const el = document.createElement('div');
      el.className = 'pe-zone';
      el.setAttribute('data-pe-zone', '');
      el.dataset.zoneId = z.id;
      el.style.left = z.x * 100 + '%';
      el.style.top = z.y * 100 + '%';
      el.style.width = z.width * 100 + '%';
      el.style.height = z.height * 100 + '%';
      if (String(z.id) === String(this.selectedZoneId)) el.classList.add('is-selected');

      const hasDesign = !!this.designs[this.currentAngle + ':' + z.id];
      if (hasDesign) el.classList.add('has-design');

      // libellé de zone : "Zone logo" (hérité du dashboard) devient "Zone design"
      let zoneTag = z.label;
      if (zoneTag && /^zone\s*logo$/i.test(zoneTag.trim())) {
        zoneTag = this.t('zone_tag', 'Zone design');
      }
      el.innerHTML =
        (zoneTag ? '<span class="pe-zone__label">' + this.escape(zoneTag) + '</span>' : '') +
        '<span class="pe-zone__hint">' +
        '<span class="pe-zone__hint-icon">+</span>' +
        (this.zoneLabel
          ? '<span class="pe-zone__hint-text">' + this.escape(this.zoneLabel) + '</span>'
          : '') +
        '</span>';

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectZone(z.id);
        // zone vide : ouvrir directement l'explorateur de fichiers
        if (!hasDesign) {
          const input = this.querySelector('[data-pe-file]');
          if (input) input.click();
        }
      });

      // dépôt direct d'un fichier logo dans la zone
      ['dragenter', 'dragover'].forEach((evt) =>
        el.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
          el.classList.add('is-dragover');
        })
      );
      ['dragleave', 'drop'].forEach((evt) =>
        el.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
          el.classList.remove('is-dragover');
        })
      );
      el.addEventListener('drop', (e) => {
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f && f.type.indexOf('image/') === 0) {
          this.selectZone(z.id);
          this.loadFile(f);
        }
      });

      this.layer.appendChild(el);
    });

    // 2) Dessiner les logos de cet angle
    zones.forEach((z) => {
      const design = this.designs[this.currentAngle + ':' + z.id];
      if (design) this.renderArtwork(design, z);
    });

    this.updateControlsVisibility();
  }

  selectZone(id) {
    this.selectedZoneId = id;
    this.layer.querySelectorAll('[data-pe-zone]').forEach((el) =>
      el.classList.toggle('is-selected', String(el.dataset.zoneId) === String(id))
    );
    this.layer.querySelectorAll('[data-pe-artwork]').forEach((el) =>
      el.classList.toggle('is-selected', String(el.dataset.zoneId) === String(id))
    );
    // synchroniser les sliders sur le logo de cette zone
    const design = this.designs[this.currentAngle + ':' + id];
    const scale = this.querySelector('[data-pe-scale]');
    const rotate = this.querySelector('[data-pe-rotate]');
    if (design) {
      if (scale) scale.value = Math.round(design.w * 100);
      if (rotate) rotate.value = design.rotate;
    }
    this.syncPrintTypeRadios();
    this.updateControlsVisibility();
  }

  updateControlsVisibility() {
    const controls = this.querySelector('[data-pe-controls]');
    if (!controls) return;
    const hasSelected = this.selectedZoneId && this.designs[this.currentAngle + ':' + this.selectedZoneId];
    controls.classList.toggle('hidden', !hasSelected);
  }

  /* ---------------- Upload ---------------- */
  bindUpload() {
    const input = this.querySelector('[data-pe-file]');
    const dropzone = this.querySelector('[data-pe-dropzone]');

    if (input) {
      input.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) this.loadFile(file);
      });
    }
    if (dropzone) {
      ['dragenter', 'dragover'].forEach((evt) =>
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          dropzone.classList.add('is-dragover');
        })
      );
      ['dragleave', 'drop'].forEach((evt) =>
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
        })
      );
      dropzone.addEventListener('drop', (e) => {
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) this.loadFile(file);
      });
    }
  }

  loadFile(file) {
    // validation (alignée sur le backend : PNG/JPEG/WEBP, <= 10 Mo)
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      this.setUploadStatus(this.t('err_format', 'Format non supporté. Utilisez PNG, JPEG ou WEBP.'), 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.setUploadStatus(this.t('err_size', 'Fichier trop volumineux (max 10 Mo).'), 'error');
      return;
    }

    // zone cible : la zone sélectionnée, sinon la première de l'angle
    const zones = this.getCurrentZones();
    let zone = this.getZoneById(this.selectedZoneId) || zones[0];
    if (!zone) return;

    // popup d'optimisation (retrait de fond + trim) si le backend est configuré
    if (this.backendUrl && this.querySelector('[data-pe-modal]')) {
      this.openLogoModal(file, zone);
      return;
    }
    this.placeLogoFile(file, zone);
  }

  // Place le logo (déjà optimisé ou non) dans la zone et l'envoie au backend
  placeLogoFile(file, zone) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const w = Math.min(zone.width * 0.8, zone.width); // ratio image
      // identité de la zone, stable entre variantes (les ids de zones diffèrent
      // d'une couleur à l'autre) : placement + position + rectangle d'origine
      const zonesNow = this.getCurrentZones();
      const zoneIndex = zonesNow.findIndex((z) => String(z.id) === String(zone.id));
      const design = {
        dataUrl: e.target.result,
        fileName: file.name || 'logo.png',
        zonePlacement: zone.placement || null,
        zoneIndex: zoneIndex > -1 ? zoneIndex : 0,
        zoneRect: { x: zone.x, y: zone.y, width: zone.width, height: zone.height },
        printType: this.normalizePrintType(this.defaultPrintType),
        zoneId: zone.id,
        x: zone.x + (zone.width - w) / 2, // centré horizontalement
        y: zone.y + zone.height * 0.1,
        w: w,
        rotate: 0,
      };
      this.designs[this.currentAngle + ':' + zone.id] = design;
      this.selectedZoneId = zone.id;

      const area = this.querySelector(`[data-pe-area][data-index="${this.currentAngle}"]`);
      if (area) area.classList.add('has-design');

      this.renderStage();
      // recentrer verticalement une fois la hauteur connue, puis borner
      const el = this.layer.querySelector(`[data-pe-artwork][data-zone-id="${zone.id}"]`);
      if (el) {
        const lhRatio = el.offsetHeight / this.layer.offsetHeight;
        design.y = zone.y + (zone.height - lhRatio) / 2;
        this.clampToZone(design, zone, el);
        this.positionArtwork(el, design);
      }
      this.serialize();
      // Étape A : envoyer le logo brut + géométrie au backend
      this.uploadLogo(design, zone, file);
    };
    reader.readAsDataURL(file);
  }

  /* ---------------- Popup d'optimisation du logo (retrait de fond + trim) ----------------
     Flux (cf. AGENTS.contexte-trim.md) :
     1. sélection du fichier -> popup avec aperçu + case "retirer le fond"
     2. "Optimiser mon logo" -> POST /api/customizer/logo/trim (aperçu, ne persiste rien)
     3. "Utiliser ce logo" -> placement dans la zone + POST /uploads (trim idempotent)
  ------------------------------------------------------------------------------------- */
  bindLogoModal() {
    const modal = this.querySelector('[data-pe-modal]');
    if (!modal) return;

    modal.querySelectorAll('[data-pe-modal-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeLogoModal())
    );

    const checkbox = modal.querySelector('[data-pe-modal-removebg]');
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        // choix modifié après optimisation : on repart de l'original
        if (this.modalCtx && this.modalCtx.processed) this.resetLogoModal();
      });
    }

    const primary = modal.querySelector('[data-pe-modal-primary]');
    if (primary) {
      primary.addEventListener('click', () => {
        if (this.modalCtx && this.modalCtx.processed) this.acceptModalLogo(true);
        else this.optimizeModalLogo();
      });
    }
    const secondary = modal.querySelector('[data-pe-modal-secondary]');
    if (secondary) {
      secondary.addEventListener('click', () => {
        if (this.modalCtx && this.modalCtx.processed) this.resetLogoModal();
        else this.acceptModalLogo(false);
      });
    }
  }

  setModalStatus(message, type) {
    const el = this.querySelector('[data-pe-modal-status]');
    if (!el) return;
    if (!message) {
      el.classList.add('hidden');
      return;
    }
    el.className = 'pe-cart__status pe-cart__status--' + type;
    el.textContent = '';
    if (type === 'loading') {
      const sp = document.createElement('span');
      sp.className = 'pe-spinner';
      el.appendChild(sp);
    }
    el.appendChild(document.createTextNode(message));
  }

  setModalPreview(src) {
    const img = this.querySelector('[data-pe-modal-img]');
    if (img) img.src = src;
  }

  openLogoModal(file, zone) {
    const modal = this.querySelector('[data-pe-modal]');
    const url = URL.createObjectURL(file);
    this.modalCtx = { file: file, zone: zone, processed: null, urls: [url] };

    this.setModalPreview(url);
    const meta = this.querySelector('[data-pe-modal-meta]');
    if (meta) meta.classList.add('hidden');
    const checkbox = this.querySelector('[data-pe-modal-removebg]');
    if (checkbox) checkbox.checked = false;
    const primary = this.querySelector('[data-pe-modal-primary]');
    if (primary) {
      primary.textContent = this.t('modal_optimize', 'Optimiser mon logo');
      primary.disabled = false;
    }
    const secondary = this.querySelector('[data-pe-modal-secondary]');
    if (secondary) secondary.textContent = this.t('modal_use_asis', 'Utiliser tel quel');
    this.setModalStatus('', 'loading');

    modal.classList.remove('hidden');
  }

  // Retour à l'état initial (aperçu original, avant optimisation)
  resetLogoModal() {
    const ctx = this.modalCtx;
    if (!ctx) return;
    ctx.processed = null;
    this.setModalPreview(ctx.urls[0]);
    const meta = this.querySelector('[data-pe-modal-meta]');
    if (meta) meta.classList.add('hidden');
    const primary = this.querySelector('[data-pe-modal-primary]');
    if (primary) {
      primary.textContent = this.t('modal_optimize', 'Optimiser mon logo');
      primary.disabled = false;
    }
    const secondary = this.querySelector('[data-pe-modal-secondary]');
    if (secondary) secondary.textContent = this.t('modal_use_asis', 'Utiliser tel quel');
    this.setModalStatus('', 'loading');
  }

  closeLogoModal() {
    const modal = this.querySelector('[data-pe-modal]');
    if (modal) modal.classList.add('hidden');
    if (this.modalCtx) {
      this.modalCtx.urls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (e) {}
      });
      this.modalCtx = null;
    }
    // permet de re-sélectionner le même fichier
    const input = this.querySelector('[data-pe-file]');
    if (input) input.value = '';
  }

  /* ---- Retrait de fond CÔTÉ CLIENT (@imgly/background-removal, IA/WASM) ----
     cf. AGENTS.contexte-removebg.md : le backend ne fait QUE le trim ;
     ne jamais lui envoyer removeBackground (méthode server-side dépréciée). */
  getBgRemovalModule() {
    if (!this._bgRemovalPromise) {
      // /+esm : jsDelivr réécrit les imports nus (onnxruntime-web…) en URLs
      // complètes — indispensable sans bundler dans un thème Shopify.
      // Les assets du modèle restent servis par le CDN IMG.LY (publicPath par défaut).
      this._bgRemovalPromise = import(
        'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1/+esm'
      ).then((mod) => {
        console.debug('[pe-bg] module de détourage chargé');
        return mod;
      });
      // en cas d'échec, permettre une nouvelle tentative plus tard
      this._bgRemovalPromise.catch(() => {
        this._bgRemovalPromise = null;
      });
    }
    return this._bgRemovalPromise;
  }

  // Réduit les très grandes images avant détourage (vitesse + limite Vercel ~4,5 Mo)
  async downscaleForRemoval(file, maxSide) {
    try {
      const url = URL.createObjectURL(file);
      const img = await this.loadImage(url, false);
      URL.revokeObjectURL(url);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h || Math.max(w, h) <= maxSide) return file;
      const scale = maxSide / Math.max(w, h);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      return blob || file;
    } catch (e) {
      return file;
    }
  }

  async aiRemoveBackground(file) {
    const mod = await this.getBgRemovalModule();
    const input = await this.downscaleForRemoval(file, 2500);
    return await mod.removeBackground(input, {
      model: 'isnet', // qualité maximale (modèle complet, ~44 Mo au 1er usage)
      output: { format: 'image/png' }, // PNG => transparence
      progress: (key, current, total) => {
        const pct = total ? Math.round((current / total) * 100) : 0;
        if (String(key).indexOf('fetch') === 0) {
          this.setModalStatus(
            this.t('downloading_model', 'Téléchargement du modèle IA…') +
              ' ' + pct + ' % (' + this.t('first_time_only', 'une seule fois') + ')',
            'loading'
          );
        } else {
          this.setModalStatus(
            this.t('removing_bg', 'Détourage du logo en cours…') + ' ' + pct + ' %',
            'loading'
          );
        }
      },
    });
  }

  /* ---- Détourage par couleur (fond uni) ----
     Sur un logo posé sur fond uni — la grande majorité des cas — cette méthode
     donne des contours BIEN plus nets que l'IA (qui est entraînée sur des photos).
     Retrait GLOBAL de la couleur de fond : les zones fermées (intérieur d'un « O »,
     d'un « A », entre deux lettres) sont elles aussi rendues transparentes, sinon
     on imprimerait un aplat plein dans le trou de la lettre. */

  // Couleur dominante du pourtour + son homogénéité (0..1)
  analyzeEdgeColor(data, w, h) {
    const px = [];
    const step = Math.max(1, Math.floor(Math.min(w, h) / 120));
    const push = (x, y) => {
      const i = (y * w + x) * 4;
      if (data[i + 3] < 8) return; // déjà transparent : on ignore
      px.push([data[i], data[i + 1], data[i + 2]]);
    };
    for (let x = 0; x < w; x += step) {
      push(x, 0);
      push(x, h - 1);
    }
    for (let y = 0; y < h; y += step) {
      push(0, y);
      push(w - 1, y);
    }
    if (px.length < 8) return null;

    // couleur dominante par quantification grossière (paquets de 16 niveaux)
    const buckets = {};
    px.forEach((p) => {
      const k = ((p[0] >> 4) << 16) | ((p[1] >> 4) << 8) | (p[2] >> 4);
      (buckets[k] = buckets[k] || []).push(p);
    });
    let bestKey = null;
    Object.keys(buckets).forEach((k) => {
      if (!bestKey || buckets[k].length > buckets[bestKey].length) bestKey = k;
    });
    const grp = buckets[bestKey];
    const avg = [0, 0, 0];
    grp.forEach((p) => {
      avg[0] += p[0];
      avg[1] += p[1];
      avg[2] += p[2];
    });
    return {
      color: [
        Math.round(avg[0] / grp.length),
        Math.round(avg[1] / grp.length),
        Math.round(avg[2] / grp.length),
      ],
      ratio: grp.length / px.length,
    };
  }

  // Renvoie un Blob PNG détouré, ou null si le fond n'est pas assez uni
  async colorKeyRemoveBackground(file) {
    const url = URL.createObjectURL(file);
    let img;
    try {
      img = await this.loadImage(url, false);
    } finally {
      URL.revokeObjectURL(url);
    }
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return null;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;

    const bg = this.analyzeEdgeColor(data, w, h);
    // fond hétérogène (photo, dégradé) -> laisser l'IA faire le travail
    if (!bg || bg.ratio < 0.7) return null;

    const [br, bg_, bb] = bg.color;
    const HARD = 34; // en deçà : fond pur (alpha 0)
    const SOFT = 92; // au delà : logo pur (conservé)
    const dist = (i) => {
      const dr = data[i] - br;
      const dg = data[i + 1] - bg_;
      const db = data[i + 2] - bb;
      return Math.sqrt(dr * dr + dg * dg + db * db);
    };

    // Retrait GLOBAL de la couleur de fond : où qu'il soit dans l'image, donc
    // y compris dans les zones fermées (trou du « O », contre-formes des lettres).
    const total = w * h;
    let removed = 0;
    for (let p = 0; p < total; p++) {
      const i = p * 4;
      if (data[i + 3] === 0) continue; // déjà transparent
      const d = dist(i);
      if (d >= SOFT) continue; // clairement du logo : on conserve
      let a;
      if (d <= HARD) a = 0;
      else a = Math.round((255 * (d - HARD)) / (SOFT - HARD));
      if (a === 0) {
        data[i + 3] = 0;
        removed++;
      } else if (a < 255) {
        // couleur observée = a*logo + (1-a)*fond -> on retrouve la couleur du logo
        const af = a / 255;
        data[i] = Math.max(0, Math.min(255, (data[i] - (1 - af) * br) / af));
        data[i + 1] = Math.max(0, Math.min(255, (data[i + 1] - (1 - af) * bg_) / af));
        data[i + 2] = Math.max(0, Math.min(255, (data[i + 2] - (1 - af) * bb) / af));
        data[i + 3] = Math.min(data[i + 3], a);
        removed += 1 - af;
      }
    }

    const pctRemoved = removed / total;
    // rien retiré (déjà transparent) ou tout retiré (logo mangé) -> abandon
    if (pctRemoved < 0.02 || pctRemoved > 0.97) return null;

    ctx.putImageData(imgData, 0, 0);
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  }

  // Dispatcher : couleur si fond uni (net), sinon IA. -> { blob, method }
  async clientRemoveBackground(file) {
    this.setModalStatus(this.t('analyzing', 'Analyse du fond…'), 'loading');
    try {
      const keyed = await this.colorKeyRemoveBackground(file);
      if (keyed) {
        console.debug('[pe-bg] détourage par couleur (fond uni)');
        return { blob: keyed, method: 'color' };
      }
    } catch (e) {
      console.warn('[pe-bg] détourage par couleur indisponible :', e);
    }
    console.debug('[pe-bg] fond complexe -> détourage IA');
    const blob = await this.aiRemoveBackground(file);
    return { blob: blob, method: 'ai' };
  }

  // (option) détourage IA côté client, puis POST /logo/trim — aperçu, sans persistance
  async optimizeModalLogo() {
    const ctx = this.modalCtx;
    if (!ctx) return;
    const checkbox = this.querySelector('[data-pe-modal-removebg]');
    const removeBg = !!(checkbox && checkbox.checked);
    const primary = this.querySelector('[data-pe-modal-primary]');
    if (primary) primary.disabled = true;

    // 1) retrait de fond dans le navigateur (repli sur l'original, mais signalé)
    let working = ctx.file;
    let bgRemoved = false;
    let bgFailed = false;
    let bgMethod = null;
    if (removeBg) {
      this.setModalStatus(this.t('preparing_removal', 'Préparation du détourage…'), 'loading');
      try {
        const out = await this.clientRemoveBackground(ctx.file);
        working = out.blob;
        bgMethod = out.method;
        bgRemoved = true;
      } catch (e) {
        console.warn('[pe-bg] retrait de fond échoué, on garde l’original :', e);
        working = ctx.file;
        bgFailed = true;
      }
    }

    // 2) trim serveur (recadrage de la bande transparente)
    this.setModalStatus(this.t('trimming', 'Recadrage du logo…'), 'loading');
    try {
      const fd = new FormData();
      fd.append('file', working, 'logo.png');
      // NE PAS envoyer removeBackground : détourage déjà fait côté client
      const res = await fetch(this.backendUrl + '/api/customizer/logo/trim', {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        let data = {};
        try {
          data = await res.json();
        } catch (e) {}
        throw new Error(data.error || 'Erreur ' + res.status);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      ctx.urls.push(url);
      ctx.processed = {
        blob: blob,
        backgroundRemoved: bgRemoved,
        trimmed: res.headers.get('X-Trimmed') === 'true',
        width: parseInt(res.headers.get('X-Logo-Width'), 10) || null,
        height: parseInt(res.headers.get('X-Logo-Height'), 10) || null,
      };

      this.setModalPreview(url);
      const meta = this.querySelector('[data-pe-modal-meta]');
      if (meta) {
        const parts = [];
        if (ctx.processed.backgroundRemoved) {
          parts.push(
            bgMethod === 'color'
              ? this.t('bg_removed_sharp', 'Fond retiré (contours nets) ✓')
              : this.t('bg_removed_ai', 'Fond retiré par IA ✓')
          );
        }
        if (ctx.processed.trimmed) parts.push(this.t('trimmed', 'Recadré ✓'));
        if (!parts.length) {
          parts.push(
            bgFailed
              ? this.t('bg_failed', 'Fond non retiré (échec du détourage)')
              : this.t('already_optimal', 'Déjà optimal — aucune modification nécessaire')
          );
        }
        if (ctx.processed.width && ctx.processed.height) {
          parts.push(ctx.processed.width + ' × ' + ctx.processed.height + ' px');
        }
        meta.textContent = parts.join(' · ');
        meta.classList.remove('hidden');
      }
      if (primary) {
        primary.textContent = this.t('modal_use', 'Utiliser ce logo');
        primary.disabled = false;
      }
      const secondary = this.querySelector('[data-pe-modal-secondary]');
      if (secondary) secondary.textContent = this.t('modal_retry', 'Recommencer');
      if (bgFailed) {
        this.setModalStatus(
          this.t(
            'bg_failed_status',
            'Logo recadré, mais le détourage du fond a échoué — cliquez « Recommencer » pour réessayer, ou utilisez-le tel quel.'
          ),
          'error'
        );
      } else {
        this.setModalStatus(this.t('optimized', 'Logo optimisé ✓'), 'success');
      }
    } catch (err) {
      this.setModalStatus(
        this.t('optimize_failed', 'Échec de l’optimisation : ') + String((err && err.message) || err),
        'error'
      );
      if (primary) primary.disabled = false;
    }
  }

  // Ferme la popup et place le logo (optimisé ou original) dans la zone
  acceptModalLogo(useProcessed) {
    const ctx = this.modalCtx;
    if (!ctx) return;
    let fileToUse = ctx.file;
    if (useProcessed && ctx.processed) {
      const type = ctx.processed.blob.type || 'image/png';
      const ext = type === 'image/webp' ? '.webp' : '.png';
      const base = String(ctx.file.name || 'logo').replace(/\.[^.]+$/, '');
      try {
        fileToUse = new File([ctx.processed.blob], base + ext, { type: type });
      } catch (e) {
        fileToUse = ctx.processed.blob;
      }
    }
    const zone = ctx.zone;
    this.closeLogoModal();
    this.placeLogoFile(fileToUse, zone);
  }

  /* ---------------- Envoi au backend (POST /api/customizer/uploads) ---------------- */
  getPlacement(angleIndex) {
    const area = this.querySelector(`[data-pe-area][data-index="${angleIndex}"]`);
    return (area && area.dataset.placement) || 'front';
  }

  setUploadStatus(message, type) {
    if (!this.uploadStatus) return;
    this.uploadStatus.className = 'pe-upload__status pe-upload__status--' + type;
    this.uploadStatus.textContent = '';
    if (type === 'loading') {
      const sp = document.createElement('span');
      sp.className = 'pe-spinner';
      this.uploadStatus.appendChild(sp);
    }
    this.uploadStatus.appendChild(document.createTextNode(message));
  }

  async uploadLogo(design, zone, file, angleIndex) {
    const placement = this.getPlacement(angleIndex != null ? angleIndex : this.currentAngle);
    design.placement = placement;

    if (!this.backendUrl) {
      this.setUploadStatus(this.t('no_backend', 'Logo ajouté (URL backend non configurée).'), 'error');
      return;
    }

    // hauteur en ratio d'image, mesurée sur l'élément rendu
    const el = this.layer.querySelector(`[data-pe-artwork][data-zone-id="${design.zoneId}"]`);
    const heightRatio = el && this.layer.offsetHeight ? el.offsetHeight / this.layer.offsetHeight : design.h || 0;

    const fd = new FormData();
    fd.append('file', file);
    if (this.dataset.productId) fd.append('productId', this.dataset.productId);
    if (this.currentVariantId) fd.append('variantId', this.currentVariantId);
    fd.append('placement', placement);
    fd.append('logoX', design.x);
    fd.append('logoY', design.y);
    fd.append('logoWidth', design.w);
    fd.append('logoHeight', heightRatio);
    fd.append('logoRotation', design.rotate || 0);

    this.setUploadStatus(this.t('uploading', 'Envoi du logo…'), 'loading');
    try {
      // NE PAS fixer Content-Type : le navigateur ajoute la boundary multipart
      const res = await fetch(this.backendUrl + '/api/customizer/uploads', {
        method: 'POST',
        body: fd,
      });
      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || 'Erreur ' + res.status);
      }
      design.logoUploadId = data.id || null;
      design.logoUrl = this.cleanUrl(data.imageUrl);
      design.uploadError = null;
      this.setUploadStatus(this.t('uploaded', 'Logo envoyé ✓'), 'success');

      // mémorise le dernier logo pour l'aperçu sur les cartes produit (logo-preview.js)
      if (design.logoUrl) {
        try {
          localStorage.setItem(
            'pe_logo_preview',
            JSON.stringify({ url: design.logoUrl, at: Date.now() })
          );
        } catch (e) {}
      }
    } catch (err) {
      design.logoUploadId = null;
      design.uploadError = String((err && err.message) || err);
      this.setUploadStatus(this.t('upload_failed', 'Échec de l’envoi : ') + design.uploadError, 'error');
    }
    this.serialize();
  }

  /* ---------------- Rendu + interaction d'un logo ---------------- */
  renderArtwork(design, zone) {
    const art = document.createElement('div');
    art.className = 'pe-artwork';
    if (String(design.zoneId) === String(this.selectedZoneId)) art.classList.add('is-selected');
    art.setAttribute('data-pe-artwork', '');
    art.dataset.zoneId = design.zoneId;
    art.innerHTML =
      '<img src="' + design.dataUrl + '" alt="artwork">' +
      '<span class="pe-artwork__handle" data-pe-handle></span>' +
      '<button type="button" class="pe-artwork__delete" data-pe-delete aria-label="' +
      this.escape(this.t('remove_design', 'Retirer ce design')) +
      '">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M10 4h4M9 7v13a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V7M10 11v6M14 11v6"/></svg>' +
      '</button>';
    this.layer.appendChild(art);
    this.positionArtwork(art, design);

    art.addEventListener('pointerdown', (e) => {
      if (e.target.closest('[data-pe-handle]') || e.target.closest('[data-pe-delete]')) return;
      this.selectZone(design.zoneId);
    });

    const del = art.querySelector('[data-pe-delete]');
    if (del) {
      del.addEventListener('pointerdown', (e) => e.stopPropagation());
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeDesign(this.currentAngle, design.zoneId);
      });
    }

    // déposer un fichier sur un logo existant = le remplacer
    ['dragenter', 'dragover'].forEach((evt) =>
      art.addEventListener(evt, (e) => {
        e.preventDefault();
        e.stopPropagation();
      })
    );
    art.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f && f.type.indexOf('image/') === 0) {
        this.selectZone(design.zoneId);
        this.loadFile(f);
      }
    });

    this.enableDrag(art, design, zone);
    this.enableResize(art.querySelector('[data-pe-handle]'), art, design, zone);
  }

  // Retire un design (poubelle sur le logo) : état + rendu.
  // Le fichier déjà envoyé reste sur le backend, qui le nettoie seul.
  removeDesign(angleIndex, zoneId) {
    const key = angleIndex + ':' + zoneId;
    const design = this.designs[key];
    if (!design) return;
    delete this.designs[key];

    if (String(this.selectedZoneId) === String(zoneId)) this.selectedZoneId = null;

    // pastille "a un design" de la vignette d'angle
    const hasAny = Object.keys(this.designs).some((k) => k.indexOf(angleIndex + ':') === 0);
    if (!hasAny) {
      const area = this.querySelector(`[data-pe-area][data-index="${angleIndex}"]`);
      if (area) area.classList.remove('has-design');
    }

    if (Number(angleIndex) === this.currentAngle) this.renderStage();
    this.serialize();
  }

  // Zone (élément DOM) correspondant à un id — la zone d'un logo est masquée,
  // on la ré-affiche en pointillés le temps d'un drag/resize (classe is-bounds).
  findZoneEl(zoneId) {
    let found = null;
    this.layer.querySelectorAll('[data-pe-zone]').forEach((z) => {
      if (String(z.dataset.zoneId) === String(zoneId)) found = z;
    });
    return found;
  }

  setZoneBounds(zoneId, on) {
    const el = this.findZoneEl(zoneId);
    if (el) el.classList.toggle('is-bounds', !!on);
  }

  positionArtwork(art, design) {
    art.style.left = design.x * 100 + '%';
    art.style.top = design.y * 100 + '%';
    art.style.width = design.w * 100 + '%';
    art.style.transform = 'rotate(' + design.rotate + 'deg)';
  }

  clamp(v, min, max) {
    if (max < min) max = min;
    return Math.max(min, Math.min(max, v));
  }

  // Borne le logo dans sa zone (coords ratios d'image).
  clampToZone(design, zone, el) {
    if (!zone || zone._implicit) {
      // pas de zone : on borne à l'image entière
      zone = { x: 0, y: 0, width: 1, height: 1 };
    }
    const lw = design.w;
    const lh = el ? el.offsetHeight / this.layer.offsetHeight : 0;
    if (lh) design.h = lh; // mémorise la hauteur en ratio d'image
    design.x = this.clamp(design.x, zone.x, zone.x + zone.width - lw);
    design.y = this.clamp(design.y, zone.y, zone.y + zone.height - lh);
  }

  enableDrag(art, design, zone) {
    art.addEventListener('pointerdown', (e) => {
      if (e.target.closest('[data-pe-handle]')) return;
      e.preventDefault();
      art.setPointerCapture(e.pointerId);
      art.classList.add('is-dragging');
      this.setZoneBounds(design.zoneId, true);

      const rect = this.layer.getBoundingClientRect();
      const startX = e.clientX;
      const startY = e.clientY;
      const origX = design.x;
      const origY = design.y;

      const move = (ev) => {
        design.x = origX + (ev.clientX - startX) / rect.width;
        design.y = origY + (ev.clientY - startY) / rect.height;
        this.clampToZone(design, zone, art);
        this.positionArtwork(art, design);
      };
      const up = (ev) => {
        art.classList.remove('is-dragging');
        this.setZoneBounds(design.zoneId, false);
        art.releasePointerCapture(ev.pointerId);
        art.removeEventListener('pointermove', move);
        art.removeEventListener('pointerup', up);
        this.serialize();
      };
      art.addEventListener('pointermove', move);
      art.addEventListener('pointerup', up);
    });
  }

  enableResize(handle, art, design, zone) {
    if (!handle) return;
    handle.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      handle.setPointerCapture(e.pointerId);
      this.setZoneBounds(design.zoneId, true);
      const rect = this.layer.getBoundingClientRect();

      const move = (ev) => {
        // largeur = distance (gauche du logo -> curseur) en ratio d'image
        const leftPx = design.x * rect.width + rect.left;
        const newW = (ev.clientX - leftPx) / rect.width;
        const maxW = zone && !zone._implicit ? zone.width : 1;
        design.w = this.clamp(newW, 0.05, maxW);
        this.clampToZone(design, zone, art);
        this.positionArtwork(art, design);
        const scaleSlider = this.querySelector('[data-pe-scale]');
        if (scaleSlider) scaleSlider.value = Math.round(design.w * 100);
      };
      const up = (ev) => {
        this.setZoneBounds(design.zoneId, false);
        handle.releasePointerCapture(ev.pointerId);
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        this.serialize();
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
    });
  }

  /* ---------------- Sliders + reset ---------------- */
  bindControls() {
    const scale = this.querySelector('[data-pe-scale]');
    const rotate = this.querySelector('[data-pe-rotate]');
    const reset = this.querySelector('[data-pe-reset]');
    const notes = this.querySelector('[data-pe-notes]');

    const current = () => this.designs[this.currentAngle + ':' + this.selectedZoneId];
    const currentEl = () =>
      this.layer.querySelector(`[data-pe-artwork][data-zone-id="${this.selectedZoneId}"]`);

    const apply = () => {
      const design = current();
      const el = currentEl();
      const zone = this.getZoneById(this.selectedZoneId);
      if (design && el) {
        this.clampToZone(design, zone, el);
        this.positionArtwork(el, design);
      }
      this.serialize();
    };

    if (scale) {
      scale.addEventListener('input', () => {
        const design = current();
        const zone = this.getZoneById(this.selectedZoneId);
        if (design) {
          const maxW = zone && !zone._implicit ? zone.width : 1;
          design.w = Math.min(Number(scale.value) / 100, maxW);
          apply();
        }
      });
    }
    if (rotate) {
      rotate.addEventListener('input', () => {
        const design = current();
        if (design) {
          design.rotate = Number(rotate.value);
          apply();
        }
      });
    }
    if (reset) {
      reset.addEventListener('click', () => {
        const design = current();
        const zone = this.getZoneById(this.selectedZoneId);
        if (design && zone) {
          const w = Math.min(zone.width * 0.8, zone.width);
          design.w = w;
          design.rotate = 0;
          design.x = zone.x + (zone.width - w) / 2;
          design.y = zone.y + zone.height * 0.1;
          if (scale) scale.value = Math.round(w * 100);
          if (rotate) rotate.value = 0;
          apply();
        }
      });
    }
    if (notes) notes.addEventListener('input', () => this.serialize());
  }

  repositionAll() {
    const zones = this.getCurrentZones();
    this.layer.querySelectorAll('[data-pe-artwork]').forEach((el) => {
      const design = this.designs[this.currentAngle + ':' + el.dataset.zoneId];
      const zone = zones.find((z) => String(z.id) === String(el.dataset.zoneId));
      if (design) {
        this.clampToZone(design, zone, el);
        this.positionArtwork(el, design);
      }
    });
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  /* ---------------- Étape B : composite + ajout au panier ---------------- */
  bindCart() {
    const btn = this.querySelector('[data-pe-add]');
    if (btn) btn.addEventListener('click', () => this.addToCart());
  }

  setCartStatus(message, type) {
    const el = this.querySelector('[data-pe-cart-status]');
    if (!el) return;
    el.classList.remove('hidden');
    el.className = 'pe-cart__status pe-cart__status--' + type;
    el.textContent = '';
    if (type === 'loading') {
      const sp = document.createElement('span');
      sp.className = 'pe-spinner';
      el.appendChild(sp);
    }
    el.appendChild(document.createTextNode(message));
  }

  // Nettoie une URL renvoyée par le backend (retire guillemets / chevrons parasites)
  cleanUrl(u) {
    if (!u) return null;
    const s = String(u)
      .replace(/[<>"'\\]/g, '')
      .trim();
    return s || null;
  }

  loadImage(src, crossOrigin) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  // Compose l'image produit (angle) + TOUS les logos de cet angle -> Blob
  async composeAngle(angleIndex, designs, variantId) {
    const productImg = await this.loadImage(this.getAngleImage(angleIndex, variantId), true);

    // Bride la résolution de l'aperçu : une image d'angle non redimensionnée
    // (cas `generic_file` de pe-image-url.liquid) produisait un fichier de
    // plusieurs Mo, refusé à l'envoi — l'angle partait alors sans aperçu.
    const natW = productImg.naturalWidth || 1200;
    const natH = productImg.naturalHeight || 1200;
    const fit = Math.min(1, 1400 / Math.max(natW, natH));
    const W = Math.round(natW * fit);
    const H = Math.round(natH * fit);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(productImg, 0, 0, W, H);

    for (const design of designs) {
      const logoImg = await this.loadImage(design.dataUrl, false);
      const lw = design.w * W;
      const lh =
        design.h != null ? design.h * H : lw * (logoImg.naturalHeight / logoImg.naturalWidth);
      const cx = design.x * W + lw / 2;
      const cy = design.y * H + lh / 2;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(((design.rotate || 0) * Math.PI) / 180);
      ctx.drawImage(logoImg, -lw / 2, -lh / 2, lw, lh);
      ctx.restore();
    }

    return await this.canvasToPreviewBlob(canvas);
  }

  scaleCanvas(canvas, scale) {
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(canvas.width * scale));
    out.height = Math.max(1, Math.round(canvas.height * scale));
    out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
    return out;
  }

  // Exporte le canvas en restant SOUS la limite de corps de requête de Vercel
  // (~4,5 Mo). JPEG d'abord : l'image produit est opaque, la transparence est
  // inutile pour un aperçu, et le fichier est ~5x plus petit qu'en PNG.
  async canvasToPreviewBlob(canvas) {
    const MAX_BYTES = 3.5 * 1024 * 1024;
    const attempts = [
      { quality: 0.92, scale: 1 },
      { quality: 0.8, scale: 1 },
      { quality: 0.8, scale: 0.6 },
    ];
    let last = null;
    for (const a of attempts) {
      const src = a.scale === 1 ? canvas : this.scaleCanvas(canvas, a.scale);
      const blob = await new Promise((resolve) => src.toBlob(resolve, 'image/jpeg', a.quality));
      if (!blob) continue;
      last = blob;
      if (blob.size <= MAX_BYTES) return blob;
    }
    return last;
  }

  // Envoie l'aperçu composé à /api/customizer/designs
  async uploadDesign(angleIndex, design, blob, variantId) {
    const vid = variantId || this.currentVariantId;
    const area = this.querySelector(`[data-pe-area][data-index="${angleIndex}"]`);
    const placement = area ? area.dataset.placement : 'front';
    if (!blob) throw new Error("export de l'aperçu impossible (canvas)");
    const fd = new FormData();
    fd.append('file', blob, 'apercu.jpg');
    if (design.logoUploadId) fd.append('logoUploadId', design.logoUploadId);
    if (this.dataset.productId) fd.append('productId', this.dataset.productId);
    if (vid) fd.append('variantId', vid);
    fd.append('placement', placement);
    fd.append('logoX', design.x);
    fd.append('logoY', design.y);
    fd.append('logoWidth', design.w);
    fd.append('logoHeight', design.h != null ? design.h : '');
    fd.append('logoRotation', design.rotate || 0);
    fd.append('productImageUrl', this.getAngleImage(angleIndex, vid));

    const res = await fetch(this.backendUrl + '/api/customizer/designs', {
      method: 'POST',
      body: fd,
    });
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    if (!res.ok || data.ok === false) throw new Error(data.error || 'Erreur ' + res.status);
    return {
      designId: data.id || null,
      previewUrl: this.cleanUrl(data.imageUrl),
      placement: placement,
      angleName: area ? area.dataset.name : '',
    };
  }

  // Compose + enregistre les aperçus (un design par angle) pour une variante donnée.
  // includeLogoIds : n'attacher les ids des logos qu'à UN groupe (évite qu'un retrait
  // partiel du panier supprime le fichier logo partagé par les autres groupes).
  async buildDesignProps(variantId, includeLogoIds) {
    const properties = {};
    let primaryPreview = null;
    let primaryDesignId = null;
    const keys = Object.keys(this.designs);
    if (keys.length === 0 || !this.backendUrl) {
      return { properties, primaryDesignId, primaryPreview };
    }

    const byAngle = {};
    keys.forEach((key) => {
      const angleIndex = Number(key.split(':')[0]);
      (byAngle[angleIndex] = byAngle[angleIndex] || []).push(this.designs[key]);
    });

    for (const angleStr of Object.keys(byAngle)) {
      const angleIndex = Number(angleStr);
      const angleDesigns = byAngle[angleStr];
      const area = this.querySelector(`[data-pe-area][data-index="${angleIndex}"]`);
      const placement = area ? area.dataset.placement : 'front';
      const angleName = area ? area.dataset.name : '';
      const logoIds = angleDesigns.map((d) => d.logoUploadId).filter(Boolean);

      // mesure cm + type d'impression de chaque logo (infos lisibles sur la commande)
      const sizeLabels = [];
      const typeLabels = [];
      angleDesigns.forEach((d) => {
        const size = this.logoSizeCm(d, angleIndex);
        sizeLabels.push(
          size ? size.widthCm.toFixed(1) + ' × ' + size.heightCm.toFixed(1) + ' cm' : 'Non mesurée'
        );
        typeLabels.push(this.normalizePrintType(d.printType) === 'embroidery' ? 'Broderie' : 'DTF');
      });
      if (sizeLabels.length) {
        properties["Taille d'impression " + placement] = sizeLabels.join(' | ');
      }
      if (typeLabels.length) {
        properties["Type d'impression " + placement] = typeLabels.join(' | ');
      }

      try {
        const blob = await this.composeAngle(angleIndex, angleDesigns, variantId);
        const r = await this.uploadDesign(angleIndex, angleDesigns[0], blob, variantId);
        if (r.designId) {
          properties['_design_id_' + placement] = r.designId;
          if (!primaryDesignId || placement === 'front') primaryDesignId = r.designId;
        }
        if (r.previewUrl) {
          // caché du client (préfixe _), mais visible dans la commande côté admin
          properties['_apercu_' + placement] = r.previewUrl;
          // 1ʳᵉ image (front si dispo) = miniature de la ligne panier
          if (!primaryPreview || placement === 'front') primaryPreview = r.previewUrl;
        }
      } catch (e) {
        // Composite impossible : la ligne part avec les logos mais SANS aperçu.
        // Trace l'échec dans la commande (clé `_` : invisible du client, lisible
        // dans l'admin) pour qu'il ne passe plus inaperçu.
        console.error('[pe] aperçu non généré pour', placement, e);
        properties['_design_error_' + placement] = String((e && e.message) || e).slice(0, 180);
      }
      // tous les logos de l'angle (séparés par des virgules)
      if (includeLogoIds && logoIds.length) {
        properties['_logo_upload_id_' + placement] = logoIds.join(',');
      }
      if (angleName) properties['Emplacement ' + placement] = angleName;
    }

    return { properties, primaryDesignId, primaryPreview };
  }

  async addToCart() {
    const btn = this.querySelector('[data-pe-add]');
    const keys = Object.keys(this.designs); // logo optionnel : peut être vide
    if (btn) btn.disabled = true;

    const properties = {};
    let primaryPreview = null;
    let primaryDesignId = null; // clé de liaison article <-> frais
    // 1 frais PAR logo (même calcul que l'affichage sous le prix)
    const feeCounts = this.computeFeeCounts();
    const smallFeeCount = feeCounts.small;
    const largeFeeCount = feeCounts.large;
    const embroideryFeeCount = feeCounts.embroidery;

    try {
      // 1) composer + enregistrer les aperçus (un design par angle)
      if (this.backendUrl && keys.length > 0) {
        this.setCartStatus(this.t('preparing_preview', 'Préparation de l’aperçu…'), 'loading');
      }
      const built = await this.buildDesignProps(this.currentVariantId, true);
      Object.assign(properties, built.properties);
      primaryPreview = built.primaryPreview;
      primaryDesignId = built.primaryDesignId;

      // miniature de la ligne panier = composite du backend
      if (primaryPreview) properties['_preview_url'] = primaryPreview;

      // clé canonique de liaison avec les lignes de frais
      if (primaryDesignId) properties['_design_id'] = primaryDesignId;

      // nombre de frais attendus PAR UNITÉ (pour le watcher d'intégrité)
      if (primaryDesignId && keys.length > 0) {
        properties['_fee_qty_small'] = String(smallFeeCount);
        properties['_fee_qty_large'] = String(largeFeeCount);
        properties['_fee_qty_embroidery'] = String(embroideryFeeCount);
        // fixe (jamais multiplié par la quantité d'articles)
        properties['_fee_qty_digitization'] = String(feeCounts.digitization);
        if (embroideryFeeCount > 0) {
          properties['Numérisation broderie'] = this.logoDigitized
            ? 'Déjà numérisé (déclaré par le client — à vérifier)'
            : 'Incluse — ' + feeCounts.digitization + ' logo(s)';
        }
      }

      // 2) propriété technique complète (sans les dataURL)
      if (this.stateInput && this.stateInput.value) properties['_design'] = this.stateInput.value;

      // 3) ajout au panier : article + frais d'impression dans le MÊME appel
      const items = [
        { id: Number(this.currentVariantId), quantity: 1, properties: properties },
      ];

      // 1 frais PAR logo : petit (3 $) si <= 15,2 × 7 cm, grand (5 $) si un côté dépasse.
      // Les logos de même format se cumulent en quantité sur la même ligne de frais.
      if (keys.length > 0 && primaryDesignId) {
        if (smallFeeCount > 0 && this.feeVariantSmall) {
          items.push({
            id: Number(this.feeVariantSmall),
            quantity: smallFeeCount,
            properties: { _fee: 'print', _fee_for_design: primaryDesignId, _fee_size: 'small' },
          });
        }
        if (largeFeeCount > 0 && this.feeVariantLarge) {
          items.push({
            id: Number(this.feeVariantLarge),
            quantity: largeFeeCount,
            properties: { _fee: 'print', _fee_for_design: primaryDesignId, _fee_size: 'large' },
          });
        }
        if (embroideryFeeCount > 0 && this.feeVariantEmbroidery) {
          items.push({
            id: Number(this.feeVariantEmbroidery),
            quantity: embroideryFeeCount,
            properties: { _fee: 'print', _fee_for_design: primaryDesignId, _fee_size: 'embroidery' },
          });
        }
        if (feeCounts.digitization > 0 && this.feeVariantDigitization) {
          items.push({
            id: Number(this.feeVariantDigitization),
            quantity: feeCounts.digitization,
            properties: { _fee: 'print', _fee_for_design: primaryDesignId, _fee_size: 'digitization' },
          });
        }
      }

      this.setCartStatus(this.t('adding', 'Ajout au panier…'), 'loading');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!res.ok) {
        let msg = 'Erreur ' + res.status;
        try {
          const e = await res.json();
          msg = e.description || e.message || msg;
        } catch (x) {}
        throw new Error(msg);
      }

      // Mode édition : retirer l'ancienne ligne (cart-cleanup.js supprime ses
      // fichiers backend et les lignes de frais orphelines suivent le watcher)
      if (this.editLineKey) {
        this.setCartStatus(this.t('replacing', 'Remplacement de l’article…'), 'loading');
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: this.editLineKey, quantity: 0 }),
          });
        } catch (e) {
          console.warn('[pe-edit] retrait de l’ancienne ligne échoué', e);
        }
        this.editLineKey = null;
      }

      this.setCartStatus(this.t('added', 'Ajouté au panier ✓'), 'success');
      this.dispatchEvent(new CustomEvent('product-editor:added', { bubbles: true }));
      window.location.href = '/cart';
    } catch (err) {
      this.setCartStatus(this.t('failed', 'Échec : ') + String((err && err.message) || err), 'error');
      if (btn) btn.disabled = false;
    }
  }

  /* ---------------- Mode édition (retour depuis le panier) ----------------
     Le panier ajoute ?pe_edit=<line_key> au lien « Modifier le design ».
     On relit la ligne dans /cart.js, on restaure l'état depuis la propriété
     _design, et chaque logo est RÉ-UPLOADÉ au backend pour créer des fichiers
     indépendants : l'ancienne ligne peut ainsi être retirée (et nettoyée par
     cart-cleanup.js) sans casser le nouvel article. */
  async maybeRestoreFromCart() {
    let key = null;
    try {
      key = new URLSearchParams(window.location.search).get('pe_edit');
    } catch (e) {}
    if (!key) return;

    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const line = (cart.items || []).find((it) => it.key === key);
      if (!line || !line.properties || !line.properties._design) return;
      const state = JSON.parse(line.properties._design);
      this.restoreState(state, key);
    } catch (e) {
      console.warn('[pe-edit] restauration impossible', e);
    }
  }

  restoreState(state, lineKey) {
    this.editLineKey = lineKey;

    // Bandeau + libellé du bouton
    const banner = this.querySelector('[data-pe-edit-banner]');
    if (banner) banner.classList.remove('hidden');

    // 1) Variante (couleur, taille, …) : resynchronise boutons + libellés
    const variant = this.variants.find((v) => String(v.id) === String(state.variantId));
    if (variant && variant.options) {
      this.selectedOptions = variant.options.slice();
      this.querySelectorAll('[data-pe-swatch],[data-pe-pill]').forEach((btn) => {
        const idx = Number(btn.dataset.optionIndex || 0);
        const val = btn.dataset.value != null ? btn.dataset.value : btn.dataset.color;
        const isActive = String(val).toLowerCase() === String(variant.options[idx]).toLowerCase();
        btn.classList.toggle('is-active', isActive);
        if (!isActive) return;
        if (btn.hasAttribute('data-pe-swatch')) {
          this.color = val;
          const nameEl = this.querySelector('[data-pe-color-name]');
          if (nameEl) nameEl.textContent = val;
        } else {
          const nameEl = this.querySelector(`[data-pe-option-name="${idx}"]`);
          if (nameEl) nameEl.textContent = val;
        }
      });
      this.applyVariantSelection();
    }

    // 2) Notes
    const notes = this.querySelector('[data-pe-notes]');
    if (notes && state.notes) notes.value = state.notes;

    // 3) Logos par angle/zone
    const entries = Array.isArray(state.designs) ? state.designs : [];
    const restorable = entries.filter((e) => e && e.logoUrl && e.zoneId != null && e.angleIndex != null);
    if (restorable.length) {
      this.setUploadStatus(this.t('restoring', 'Restauration de votre design…'), 'loading');
      Promise.all(restorable.map((e) => this.restoreDesignEntry(e)))
        .then(() => {
          this.setUploadStatus(this.t('restored', 'Design restauré ✓ Vous pouvez le modifier.'), 'success');
          this.serialize();
        })
        .catch(() => {
          this.setUploadStatus(
            'Design partiellement restauré — vérifiez vos logos avant de mettre à jour.',
            'error'
          );
        });
    }
    this.serialize();
  }

  async restoreDesignEntry(entry) {
    const key = entry.angleIndex + ':' + entry.zoneId;
    const design = {
      dataUrl: entry.logoUrl, // affichage immédiat depuis l'URL distante
      fileName: entry.fileName || 'logo.png',
      zoneId: entry.zoneId,
      x: entry.x,
      y: entry.y,
      w: entry.width,
      h: entry.height != null ? entry.height : null,
      rotate: entry.rotate || 0,
      placement: entry.placement || 'front',
      logoUploadId: null, // nouveau fichier créé au ré-upload ci-dessous
      logoUrl: entry.logoUrl,
    };
    this.designs[key] = design;

    const area = this.querySelector(`[data-pe-area][data-index="${entry.angleIndex}"]`);
    if (area) area.classList.add('has-design');
    if (Number(entry.angleIndex) === Number(this.currentAngle)) this.renderStage();

    // Ré-upload : télécharge le logo existant puis le renvoie au backend
    // (fichiers indépendants + dataURL locale pour un composite canvas sain)
    const res = await fetch(entry.logoUrl, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch logo ' + res.status);
    const blob = await res.blob();
    const file = new File([blob], design.fileName, { type: blob.type || 'image/png' });

    design.dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    if (Number(entry.angleIndex) === Number(this.currentAngle)) this.renderStage();

    const zone = this.getZonesForAngle(entry.angleIndex).find(
      (z) => String(z.id) === String(entry.zoneId)
    );
    await this.uploadLogo(design, zone || null, file, Number(entry.angleIndex));
  }

  /* ---------------- Achat groupé (UI principale) ---------------- */
  bindBulk() {
    const panel = this.querySelector('[data-pe-bulk-panel]');
    if (!panel) {
      // produit sans options (variante unique) : achat direct, quantité 1
      const soloBtn = this.querySelector('[data-pe-bulk-add]');
      if (soloBtn) {
        soloBtn.disabled = false;
        soloBtn.addEventListener('click', () => this.bulkAddToCart());
      }
      return;
    }

    this.sizeValues = this.parseJSON('[data-pe-sizes]', []);
    this.colorOptionIndex = parseInt(this.dataset.colorOptionIndex, 10);
    if (isNaN(this.colorOptionIndex)) this.colorOptionIndex = -1;
    this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
    if (isNaN(this.sizeOptionIndex)) this.sizeOptionIndex = -1;

    // Clic couleur : bascule l'aperçu (images + zones) ET ajoute le groupe
    // de quantités s'il n'existe pas. Le retrait se fait via le ✕ du groupe.
    this.querySelectorAll('[data-pe-bulk-color]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const color = btn.dataset.value;
        this.previewColor(color);

        let existing = null;
        this.querySelectorAll('.pe-bulk-group').forEach((g) => {
          if (g.dataset.color === color) existing = g;
        });
        if (!existing) {
          this.addBulkGroup(color, btn.style.getPropertyValue('--pe-swatch-bg'));
          btn.classList.add('is-active');
          this.updateBulkSummary();
        }
      });
    });

    const addBtn = this.querySelector('[data-pe-bulk-add]');
    if (addBtn) addBtn.addEventListener('click', () => this.bulkAddToCart());

    // pas d'option couleur : un seul groupe (tailles), créé d'office
    if (this.colorOptionIndex === -1 && !this.querySelector('.pe-bulk-group')) {
      this.addBulkGroup(null, '');
      const empty = this.querySelector('[data-pe-bulk-empty]');
      if (empty) empty.classList.add('hidden');
    }

    // pré-ajoute la couleur sélectionnée initialement
    const initBtn = this.querySelector('[data-pe-bulk-color].is-active');
    if (initBtn && !this.querySelector('.pe-bulk-group')) {
      this.addBulkGroup(initBtn.dataset.value, initBtn.style.getPropertyValue('--pe-swatch-bg'));
    }

    this._bulkReady = true;
  }

  // Bascule l'aperçu (stage, vignettes d'angles, zones, prix) sur une couleur
  previewColor(color) {
    if (this.colorOptionIndex === -1 || color == null) return;
    this.color = color;
    this.selectedOptions[this.colorOptionIndex] = color;
    const nameEl = this.querySelector('[data-pe-color-name]');
    if (nameEl) nameEl.textContent = color;
    this.applyVariantSelection();
  }

  // Variante correspondant à (couleur, taille), les autres options = sélection courante
  findVariantByOptions(colorValue, sizeValue) {
    const ci = this.colorOptionIndex;
    const si = this.sizeOptionIndex;
    return this.variants.find((v) => {
      if (!v.options) return false;
      return v.options.every((o, i) => {
        if (i === ci && colorValue != null) {
          return String(o).toLowerCase() === String(colorValue).toLowerCase();
        }
        if (i === si && sizeValue) {
          return String(o).toLowerCase() === String(sizeValue).toLowerCase();
        }
        if (i === ci || i === si) return true;
        return String(o).toLowerCase() === String(this.selectedOptions[i] || '').toLowerCase();
      });
    });
  }

  addBulkGroup(color, swatchBg) {
    const wrap = this.querySelector('[data-pe-bulk-groups]');
    if (!wrap) return;
    const empty = wrap.querySelector('[data-pe-bulk-empty]');
    if (empty) empty.classList.add('hidden');

    const group = document.createElement('div');
    group.className = 'pe-bulk-group';
    if (color != null) group.dataset.color = color;

    if (color != null) {
      const head = document.createElement('div');
      head.className = 'pe-bulk-group__head';

      const dot = document.createElement('span');
      dot.className = 'pe-bulk-group__swatch';
      if (swatchBg) dot.style.setProperty('--pe-swatch-bg', swatchBg);
      head.appendChild(dot);

      const name = document.createElement('span');
      name.className = 'pe-bulk-group__name';
      name.textContent = color;
      head.appendChild(name);

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'pe-bulk-group__remove';
      rm.setAttribute('aria-label', this.t('remove', 'Retirer') + ' ' + color);
      rm.innerHTML = '&times;';
      rm.addEventListener('click', () => {
        group.remove();
        this.querySelectorAll('[data-pe-bulk-color]').forEach((b) => {
          if (b.dataset.value === color) b.classList.remove('is-active');
        });
        if (!this.querySelector('.pe-bulk-group') && empty) empty.classList.remove('hidden');
        this.updateBulkSummary();
      });
      head.appendChild(rm);
      group.appendChild(head);
    }

    const sizesWrap = document.createElement('div');
    sizesWrap.className = 'pe-bulk-group__sizes';
    const sizes = this.sizeValues.length ? this.sizeValues : [''];
    sizes.forEach((size) => {
      const variant = this.findVariantByOptions(color, size);
      const label = document.createElement('label');
      label.className = 'pe-bulk-size';

      const span = document.createElement('span');
      span.textContent = size || this.t('qty', 'Qté');
      label.appendChild(span);

      const input = document.createElement('input');
      input.type = 'number';
      input.min = '0';
      input.step = '1';
      input.placeholder = '0';
      input.inputMode = 'numeric';
      input.dataset.size = size;
      if (!variant || !variant.available) {
        input.disabled = true;
        label.classList.add('is-off');
        label.title = variant ? this.t('sold_out', 'Épuisé') : this.t('unavailable', 'Indisponible');
      }
      input.addEventListener('input', () => this.updateBulkSummary());
      label.appendChild(input);
      sizesWrap.appendChild(label);
    });
    group.appendChild(sizesWrap);
    wrap.appendChild(group);
  }

  // Sélection bulk courante : [{ color, lines: [{ variant, qty }] }]
  getBulkSelection() {
    const sel = [];
    this.querySelectorAll('.pe-bulk-group').forEach((group) => {
      const color = group.dataset.color != null ? group.dataset.color : null;
      const lines = [];
      group.querySelectorAll('input[type="number"]').forEach((input) => {
        const qty = parseInt(input.value, 10) || 0;
        if (qty > 0) {
          const variant = this.findVariantByOptions(color, input.dataset.size);
          if (variant) lines.push({ variant: variant, qty: qty });
        }
      });
      if (lines.length) sel.push({ color: color, lines: lines });
    });
    return sel;
  }

  updateBulkSummary() {
    const summary = this.querySelector('[data-pe-bulk-summary]');
    const addBtn = this.querySelector('[data-pe-bulk-add]');
    if (!summary) return;

    const sel = this.getBulkSelection();
    let count = 0;
    let subtotal = 0;
    sel.forEach((g) =>
      g.lines.forEach((l) => {
        count += l.qty;
        subtotal += (l.variant.price || 0) * l.qty;
      })
    );

    if (count === 0) {
      summary.classList.add('hidden');
      if (addBtn) addBtn.disabled = true;
      if (this._minWarnShown) {
        const st = this.querySelector('[data-pe-bulk-status]');
        if (st) st.classList.add('hidden');
        this._minWarnShown = false;
      }
      this.updateVolumeIncentive();
      return;
    }

    const feeCounts = this.computeFeeCounts();
    const fees =
      (feeCounts.small * this.feePriceSmall +
        feeCounts.large * this.feePriceLarge +
        feeCounts.embroidery * this.feePriceEmbroidery) *
        count +
      feeCounts.digitization * this.feePriceDigitization; // une fois, pas × quantité

    summary.classList.remove('hidden');

    // broderie : quantité totale minimale (ex. 12 articles)
    const needsMin = feeCounts.embroidery > 0 && count < this.embroideryMin;
    if (addBtn) addBtn.disabled = needsMin;
    if (needsMin) {
      this.setBulkStatus(
        this.t('embroidery_min', 'Broderie : minimum [min] articles au total — ajoutez-en encore [missing] pour continuer.')
          .replace('[min]', this.embroideryMin)
          .replace('[missing]', this.embroideryMin - count),
        'error'
      );
      this._minWarnShown = true;
    } else if (this._minWarnShown) {
      const st = this.querySelector('[data-pe-bulk-status]');
      if (st) st.classList.add('hidden');
      this._minWarnShown = false;
    }

    const countEl = this.querySelector('[data-pe-bulk-count]');
    if (countEl) {
      countEl.textContent =
        count + ' ' + (count > 1 ? this.t('items', 'articles') : this.t('item', 'article'));
    }
    const subEl = this.querySelector('[data-pe-bulk-subtotal]');
    if (subEl) subEl.textContent = this.formatMoney(subtotal);
    const feesRow = this.querySelector('[data-pe-bulk-fees-row]');
    const feesEl = this.querySelector('[data-pe-bulk-fees]');
    if (feesRow) feesRow.classList.toggle('hidden', fees === 0);
    if (feesEl) feesEl.textContent = this.formatMoney(fees);
    const totalEl = this.querySelector('[data-pe-bulk-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(subtotal + fees);

    this.updateVolumeIncentive();
  }

  setBulkStatus(message, type) {
    const el = this.querySelector('[data-pe-bulk-status]');
    if (!el) return;
    el.classList.remove('hidden');
    el.className = 'pe-cart__status pe-cart__status--' + type;
    el.textContent = '';
    if (type === 'loading') {
      const sp = document.createElement('span');
      sp.className = 'pe-spinner';
      el.appendChild(sp);
    }
    el.appendChild(document.createTextNode(message));
  }

  async bulkAddToCart() {
    const addBtn = this.querySelector('[data-pe-bulk-add]');
    let sel = this.getBulkSelection();
    // produit sans options : la sélection = variante courante, quantité 1
    if (!sel.length && !this.querySelector('[data-pe-bulk-panel]')) {
      const cur = this.variants.find((v) => String(v.id) === String(this.currentVariantId));
      if (cur) sel = [{ color: this.color, lines: [{ variant: cur, qty: 1 }] }];
    }
    if (!sel.length) {
      this.setBulkStatus(this.t('min_qty', 'Indiquez au moins une quantité.'), 'error');
      return;
    }
    if (addBtn) addBtn.disabled = true;

    const designKeys = Object.keys(this.designs);
    const feeCounts = this.computeFeeCounts();

    // broderie : quantité totale minimale (garde-fou, l'UI désactive déjà le bouton)
    const selTotal = sel.reduce(function (n, g) {
      return n + g.lines.reduce(function (m, l) {
        return m + l.qty;
      }, 0);
    }, 0);
    if (feeCounts.embroidery > 0 && selTotal < this.embroideryMin) {
      this.setBulkStatus(
        this.t('embroidery_min', 'Broderie : minimum [min] articles au total — ajoutez-en encore [missing] pour continuer.')
          .replace('[min]', this.embroideryMin)
          .replace('[missing]', this.embroideryMin - selTotal),
        'error'
      );
      if (addBtn) addBtn.disabled = false;
      return;
    }

    try {
      const items = [];
      let firstGroup = true;

      for (const group of sel) {
        const rep = group.lines.find((l) => l.variant.available) || group.lines[0];
        const groupQty = group.lines.reduce((n, l) => n + l.qty, 0);

        // aperçus composés sur la couleur du groupe (design par couleur)
        let props = {};
        if (designKeys.length > 0 && this.backendUrl) {
          this.setBulkStatus(
            this.t('preparing_previews', 'Préparation des aperçus') +
              (group.color ? ' (' + group.color + ')' : '') + '…',
            'loading'
          );
          const built = await this.buildDesignProps(String(rep.variant.id), firstGroup);
          props = built.properties;
          if (built.primaryDesignId) props['_design_id'] = built.primaryDesignId;
          if (built.primaryPreview) props['_preview_url'] = built.primaryPreview;
          props['_fee_qty_small'] = String(feeCounts.small);
          props['_fee_qty_large'] = String(feeCounts.large);
          props['_fee_qty_embroidery'] = String(feeCounts.embroidery);
          // numérisation : rattachée au PREMIER groupe seulement (une fois au total)
          props['_fee_qty_digitization'] = firstGroup ? String(feeCounts.digitization) : '0';
          if (firstGroup && feeCounts.embroidery > 0) {
            props['Numérisation broderie'] = this.logoDigitized
              ? 'Déjà numérisé (déclaré par le client — à vérifier)'
              : 'Incluse — ' + feeCounts.digitization + ' logo(s)';
          }
          if (this.stateInput && this.stateInput.value) props['_design'] = this.stateInput.value;
        }

        group.lines.forEach((l) => {
          items.push({ id: Number(l.variant.id), quantity: l.qty, properties: props });
        });

        // frais du groupe : N logos × quantité totale du groupe
        if (props['_design_id']) {
          if (feeCounts.small > 0 && this.feeVariantSmall) {
            items.push({
              id: Number(this.feeVariantSmall),
              quantity: feeCounts.small * groupQty,
              properties: { _fee: 'print', _fee_for_design: props['_design_id'], _fee_size: 'small' },
            });
          }
          if (feeCounts.large > 0 && this.feeVariantLarge) {
            items.push({
              id: Number(this.feeVariantLarge),
              quantity: feeCounts.large * groupQty,
              properties: { _fee: 'print', _fee_for_design: props['_design_id'], _fee_size: 'large' },
            });
          }
          if (feeCounts.embroidery > 0 && this.feeVariantEmbroidery) {
            items.push({
              id: Number(this.feeVariantEmbroidery),
              quantity: feeCounts.embroidery * groupQty,
              properties: { _fee: 'print', _fee_for_design: props['_design_id'], _fee_size: 'embroidery' },
            });
          }
          // numérisation : une fois PAR LOGO au total, jamais × quantité
          if (firstGroup && feeCounts.digitization > 0 && this.feeVariantDigitization) {
            items.push({
              id: Number(this.feeVariantDigitization),
              quantity: feeCounts.digitization,
              properties: { _fee: 'print', _fee_for_design: props['_design_id'], _fee_size: 'digitization' },
            });
          }
        }
        firstGroup = false;
      }

      this.setBulkStatus(this.t('adding', 'Ajout au panier…'), 'loading');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!res.ok) {
        let msg = 'Erreur ' + res.status;
        try {
          const e = await res.json();
          msg = e.description || e.message || msg;
        } catch (x) {}
        throw new Error(msg);
      }

      this.setBulkStatus(this.t('added', 'Ajouté au panier ✓'), 'success');
      this.dispatchEvent(new CustomEvent('product-editor:added', { bubbles: true }));
      window.location.href = '/cart';
    } catch (err) {
      this.setBulkStatus(this.t('failed', 'Échec : ') + String((err && err.message) || err), 'error');
      if (addBtn) addBtn.disabled = false;
    }
  }

  /* ---------------- Incitatif rabais de volume ---------------- */
  // Mêmes paliers que snippets/pe-volume-tier.liquid
  volumeInfo(total) {
    let pct = 0;
    let nextQty = 10;
    let nextLabel = '-10 %';
    if (total >= 250) {
      pct = 25;
      nextQty = 0;
    } else if (total >= 100) {
      pct = 25;
      nextQty = 250;
      nextLabel = this.t('vd_quote_label', 'une soumission personnalisée');
    } else if (total >= 50) {
      pct = 20;
      nextQty = 100;
      nextLabel = '-25 %';
    } else if (total >= 25) {
      pct = 15;
      nextQty = 50;
      nextLabel = '-20 %';
    } else if (total >= 10) {
      pct = 10;
      nextQty = 25;
      nextLabel = '-15 %';
    }

    let fill;
    if (total >= 250) fill = 100;
    else if (total >= 100) fill = 80 + ((total - 100) / 150) * 20;
    else if (total >= 50) fill = 60 + ((total - 50) / 50) * 20;
    else if (total >= 25) fill = 40 + ((total - 25) / 25) * 20;
    else if (total >= 10) fill = 20 + ((total - 10) / 15) * 20;
    else fill = (total / 10) * 20;

    return { pct: pct, nextQty: nextQty, nextLabel: nextLabel, fill: fill };
  }

  // Quantité de la sélection en cours : total bulk s'il y en a un, sinon 1 (achat simple)
  getSelectionQty() {
    let qty = 0;
    this.getBulkSelection().forEach((g) => g.lines.forEach((l) => (qty += l.qty)));
    return qty > 0 ? qty : 1;
  }

  updateVolumeIncentive() {
    const bar = this.querySelector('[data-pe-vd]');
    if (!bar) return;

    const selQty = this.getSelectionQty();
    const total = (this.cartQty || 0) + selQty;
    const info = this.volumeInfo(total);

    // remplissage : styles critiques en inline + !important (imbattables)
    const fill = bar.querySelector('[data-pe-vd-fill]');
    if (fill) {
      let pctFill = Math.min(100, info.fill);
      if (total > 0 && pctFill < 2.5) pctFill = 2.5;
      fill.style.setProperty('width', pctFill + '%', 'important');
      fill.style.setProperty('height', '100%', 'important');
      fill.style.setProperty('min-height', '10px', 'important');
      fill.style.setProperty('display', 'block', 'important');
      fill.style.setProperty('background', 'linear-gradient(90deg,#9d0c23,#d43852)', 'important');
      fill.style.setProperty('border-radius', '99px', 'important');
      fill.style.setProperty('opacity', '1', 'important');
      fill.style.setProperty('visibility', 'visible', 'important');
    }

    // badge palier débloqué
    const badge = bar.querySelector('[data-pe-vd-badge]');
    if (badge) {
      badge.classList.toggle('hidden', info.pct === 0);
      badge.textContent =
        info.pct > 0
          ? this.t('unlocked', '-[pct] % débloqué 🎉').replace('[pct]', info.pct)
          : '';
    }

    // état des jalons (coche verte / pulsation du prochain)
    bar.querySelectorAll('.pe-vd__marker').forEach((m) => {
      const q = parseInt(m.dataset.qty, 10) || 0;
      m.classList.toggle('is-reached', total >= q);
      m.classList.toggle('is-next', total < q && q === info.nextQty);
    });

    // message
    const msg = bar.querySelector('[data-pe-vd-msg]');
    if (msg) {
      const unit = total > 1 ? this.t('items', 'articles') : this.t('item', 'article');
      const head =
        '<strong>' + total + '</strong> ' + unit + ' ' +
        this.t('vd_with_cart', 'avec votre panier') + ' · ';
      if (info.nextQty === 0) {
        // priorité au courriel (mailto:) fourni par le snippet
        const url = bar.dataset.quoteHref || bar.dataset.quoteUrl || '/pages/contact';
        msg.innerHTML =
          head +
          '<a href="' + url + '" class="pe-vd__quote-link">' +
          this.t('vd_quote_msg', 'volume 250+ — écrivez-nous par courriel pour votre soumission') +
          '</a>';
      } else {
        const remaining = info.nextQty - total;
        msg.innerHTML =
          head +
          this.t('vd_more_for', 'plus que [remaining] pour débloquer [label]')
            .replace('[remaining]', '<strong>' + remaining + '</strong>')
            .replace('[label]', '<strong>' + info.nextLabel + '</strong>');
      }
    }
  }

  /* ---------------- État ---------------- */
  getState() {
    const notes = this.querySelector('[data-pe-notes]');
    const designs = Object.keys(this.designs).map((key) => {
      const [angleIndex, zoneId] = key.split(':');
      const d = this.designs[key];
      const area = this.querySelector(`[data-pe-area][data-index="${angleIndex}"]`);
      return {
        angleIndex: Number(angleIndex),
        angleName: area ? area.dataset.name : '',
        placement: area ? area.dataset.placement : 'front',
        zoneId: zoneId,
        baseImage: this.getAngleImage(Number(angleIndex)),
        image: d.dataUrl,
        fileName: d.fileName,
        // placement en ratios de l'image (0..1), origine haut-gauche
        x: d.x,
        y: d.y,
        width: d.w,
        height: d.h != null ? d.h : null,
        rotate: d.rotate,
        printType: this.normalizePrintType(d.printType),
        // références backend (après envoi à /uploads)
        logoUploadId: d.logoUploadId || null,
        logoUrl: d.logoUrl || null,
        uploadError: d.uploadError || null,
      };
    });
    return {
      productId: this.dataset.productId,
      variantId: this.currentVariantId,
      color: this.color,
      logoDigitized: this.logoDigitized,
      notes: notes ? notes.value : '',
      designs: designs,
    };
  }

  serialize() {
    this.updateFees();
    const state = this.getState();
    if (this.stateInput) {
      const light = {
        ...state,
        designs: state.designs.map(({ image, ...rest }) => rest),
      };
      this.stateInput.value = JSON.stringify(light);
    }
    this.dispatchEvent(new CustomEvent('product-editor:change', { detail: state, bubbles: true }));
  }
}

customElements.define('product-editor', ProductEditor);
